import React from "react";
import { motion } from "framer-motion";
import { Sprout, Award, Truck, Coins } from "lucide-react";

const ITEMS = [
  {
    icon: Sprout,
    title: "Direct Farm Sourcing",
    desc: "Single-estate procurement from high-altitude Idukki plantations. No middlemen. No dilution of quality.",
  },
  {
    icon: Award,
    title: "Premium Grading",
    desc: "Manually sorted across 6mm+, 7mm+, 8mm+ and Jumbo grades — only bold, deep-green pods make the cut.",
  },
  {
    icon: Truck,
    title: "Consistent Bulk Supply",
    desc: "Year-round availability from monsoon to post-harvest. Reliable container & truck-load fulfilment.",
  },
  {
    icon: Coins,
    title: "Wholesale Pricing",
    desc: "Estate-direct pricing structured for distributors, traders, hotels and food manufacturers.",
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.12, duration: 0.7, ease: "easeOut" },
  }),
};

export const WhyChooseUs = () => {
  return (
    <section
      id="why"
      data-testid="why-section"
      className="section-pad bg-[#FAF8F2] relative"
    >
      <div className="container-luxury">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
            >
              <div className="heading-eyebrow">
                <span className="line" />
                <span className="text">Why Wholesalers Choose Us</span>
              </div>
              <h2 className="font-serif text-3xl md:text-5xl leading-tight text-[#0E4D3D]">
                A trade partner built on plantation truth.
              </h2>
              <p className="mt-6 text-[#4A4A4A] leading-relaxed text-base md:text-lg max-w-md">
                Sixteen harvests in, we have refined every link of the chain —
                from picker's basket to your warehouse — so quality, price and
                supply remain unwavering.
              </p>
              <div className="mt-8 h-px w-24 bg-[#C9A227]" />
            </motion.div>
          </div>

          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-px bg-[#E8E2CF]">
            {ITEMS.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-60px" }}
                  variants={cardVariants}
                  className="bg-[#FAF8F2] p-8 md:p-10 lift group"
                  data-testid={`why-card-${i}`}
                >
                  <div className="w-12 h-12 flex items-center justify-center border border-[#0E4D3D]/20 text-[#0E4D3D] group-hover:bg-[#0E4D3D] group-hover:text-[#C9A227] transition-all duration-500">
                    <Icon className="w-5 h-5" strokeWidth={1.4} />
                  </div>
                  <h3 className="mt-6 font-serif text-xl md:text-2xl text-[#0E4D3D]">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-[#4A4A4A] text-sm md:text-[0.95rem] leading-relaxed font-light">
                    {item.desc}
                  </p>
                  <div className="mt-6 h-px w-8 bg-[#C9A227] group-hover:w-16 transition-all duration-500" />
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
